/**
 * BrandingProvider — Platform White Label Foundation.
 *
 * Fetch `/api/public/branding` sekali saat App mount, cache di React context.
 * Semua komponen dapat pakai `useBranding()` untuk akses:
 *   - branding.general.name / short_name / tagline / description
 *   - branding.visual.logo / favicon / og_image / default_banner
 *   - branding.theme.primary / secondary / accent / background / text
 *   - branding.contact.whatsapp / email / instagram / facebook / tiktok / youtube / address / maps_url
 *   - branding.footer.text / copyright / powered_by
 *
 * Effects saat branding loaded:
 *   1. Inject CSS variables ke :root — --brand-primary, --brand-secondary, dll
 *   2. Update <title> ke branding.general.name (default fallback)
 *   3. Update favicon link href
 *   4. Update meta theme-color
 *
 * Fallback: kalau fetch gagal, pakai DEFAULT_BRANDING lokal generik (netral)
 * supaya UI tetap render tanpa identitas brand yang salah.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { imageUrl } from "@/lib/api";
import { useHost } from "@/context/HostContext";
import {
  normalizeHex, radiusFor,
  readableOn, resolveTypography,
} from "@/lib/designTokens";

const DEFAULT_BRANDING = {
  general: {
    name: "",
    short_name: "",
    tagline: "",
    description: "",
  },
  visual: {
    logo: "/brand/logo-horizontal.svg",
    logo_dark: "",
    logo_horizontal: "",
    logo_scale: "",
    favicon: "/favicon.ico",
    og_image: "",
    default_banner: "",
  },
  theme: {
    primary: "#E85D04",
    secondary: "#4A2E1F",
    accent: "#FFB703",
    background: "#FAF9F6",
    text: "#0E0E0E",
    font_heading: "",
    font_body: "",
    button_style: "",
    price_color: "",
    sale_badge_color: "",
    button_primary_color: "",
    button_secondary_color: "",
    typography_preset: "",
  },
  contact: {
    whatsapp: "", email: "", instagram: "", facebook: "",
    tiktok: "", youtube: "", address: "", maps_url: "",
  },
  footer: { text: "", copyright: "", powered_by: "" },
  // Global Commerce Premium — konten storefront tenant (announcement + business CTA).
  storefront: {
    announcement_enabled: "", announcement_text: "", announcement_link: "",
    cta_enabled: "", cta_badge: "", cta_title: "", cta_description: "",
    cta_button_text: "", cta_button_action: "", cta_button_url: "",
    cta_background_style: "", cta_background_image: "",
  },
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  reload: async () => {},
});

// --- Design System v2 (Phase 2) — 10 canonical functional color tokens ------
// System defaults used when backend `design_tokens` payload is missing (older
// backend deploy / offline / failed fetch). Kept in lockstep with backend
// `services/template_dna.py::CANONICAL_TOKEN_DEFAULTS`.
const V2_DEFAULT_TOKENS = {
  primary:           "#0ea5e9",
  primary_dark:      "#0369a1",
  accent:            "#8b5cf6",
  background:        "#ffffff",
  surface:           "#ffffff",
  surface_secondary: "#f1f5f9",
  text_primary:      "#0f172a",
  text_secondary:    "#64748b",
  border:            "#e2e8f0",
  sale:              "#dc2626",
};

const V2_TOKEN_KEYS = Object.keys(V2_DEFAULT_TOKENS);

/**
 * Deterministic darken (~15% L in HSL) — mirrors backend `_derive_primary_dark`.
 * Used only as a client-side fallback when the backend didn't supply
 * `primary_dark` (older payload). Keeping the algorithm identical here means
 * hover states stay stable across payload paths.
 */
function _derivePrimaryDark(primaryHex) {
  if (typeof primaryHex !== "string") return V2_DEFAULT_TOKENS.primary_dark;
  const m = /^#([0-9a-fA-F]{6})$/.exec(primaryHex.trim());
  if (!m) return V2_DEFAULT_TOKENS.primary_dark;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 0xff) / 255;
  const g = ((int >> 8) & 0xff) / 255;
  const b = (int & 0xff) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  const l2 = Math.max(0, l - 0.15);
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r2 = l2, g2 = l2, b2 = l2;
  if (s !== 0) {
    const q = l2 < 0.5 ? l2 * (1 + s) : l2 + s - l2 * s;
    const p = 2 * l2 - q;
    r2 = hue2rgb(p, q, h + 1 / 3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1 / 3);
  }
  const to2 = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${to2(r2)}${to2(g2)}${to2(b2)}`;
}

/**
 * Resolve the 10 canonical tokens for storefront consumption.
 *
 * Priority (highest wins):
 *   1. Backend `design_tokens` object (Phase 1) — already precedence-resolved
 *      on the server (tenant override → template snapshot → legacy → defaults).
 *   2. Legacy `theme.*` shape from the same response (backward-compat when the
 *      backend deploy is older and did not emit `design_tokens`):
 *        theme.primary    → --color-primary
 *        theme.accent     → --color-accent
 *        theme.background → --color-background
 *        theme.text       → --color-text-primary
 *        theme.sale_badge_color → --color-sale (if set)
 *   3. V2_DEFAULT_TOKENS.
 *
 * `primary_dark` auto-derives from resolved `primary` if not explicitly
 * supplied (matches server behaviour).
 */
function resolveDesignTokens(payload) {
  const dt = (payload && typeof payload === "object" && payload.design_tokens)
    || null;
  const theme = (payload && payload.theme) || {};
  const out = { ...V2_DEFAULT_TOKENS };

  // Layer 2 — legacy theme.* fallback (mapped into canonical shape).
  const legacyMap = {
    primary:      theme.primary,
    accent:       theme.accent,
    background:   theme.background,
    text_primary: theme.text,
    sale:         theme.sale_badge_color,
  };
  for (const k of Object.keys(legacyMap)) {
    const v = normalizeHex(legacyMap[k]);
    if (v) out[k] = v;
  }

  // Layer 1 — backend `design_tokens` (wins).
  if (dt && typeof dt === "object") {
    for (const k of V2_TOKEN_KEYS) {
      const v = normalizeHex(dt[k]);
      if (v) out[k] = v;
    }
  }

  // primary_dark auto-derive if still absent from both layers.
  if (!dt || !normalizeHex(dt.primary_dark)) {
    // If neither backend nor legacy supplied primary_dark, derive from primary.
    out.primary_dark = _derivePrimaryDark(out.primary);
  }

  return out;
}

/**
 * Inject the 10 canonical `--color-*` variables scoped to `.storefront-shell`,
 * plus derived legacy compatibility aliases (`--brand-*`, `--commerce-*`,
 * `--content-*`, `--nav-*`, `--action-*`) that reference the v2 tokens via
 * `var(--color-*)`. This makes v2 the SINGLE SOURCE OF TRUTH — any tenant
 * override of a v2 token instantly propagates to all legacy consumers.
 *
 * Legacy aliases are ALSO emitted at `:root` (as resolved hex, not `var()`
 * refs, because `--color-*` is scoped to `.storefront-shell`) so admin/
 * platform pages that historically inherited these vars from `:root`
 * continue to work — still derived from v2, just via a different pathway.
 *
 * Uses a <style> tag (not inline on :root) so tenant tokens NEVER leak into
 * `.admin-scope` or `.platform-scope`.
 */
function applyStorefrontColorTokens(tokens) {
  if (typeof document === "undefined") return;
  const cssKeyMap = {
    primary:           "--color-primary",
    primary_dark:      "--color-primary-dark",
    accent:            "--color-accent",
    background:        "--color-background",
    surface:           "--color-surface",
    surface_secondary: "--color-surface-secondary",
    text_primary:      "--color-text-primary",
    text_secondary:    "--color-text-secondary",
    border:            "--color-border",
    sale:              "--color-sale",
  };
  const colorLines = Object.entries(cssKeyMap)
    .map(([k, cssVar]) => `  ${cssVar}: ${tokens[k]};`)
    .join("\n");

  // Legacy aliases — derived exclusively from v2 tokens. Foreground (`-fg`)
  // vars use readableOn() at emit time (cannot be expressed as var() ref).
  const saleFg = readableOn(tokens.sale);
  const primaryFg = readableOn(tokens.primary);
  const legacyAliasLines = [
    "  /* Legacy compatibility aliases — v2 tokens are SSOT, do not set these directly */",
    "  --brand-primary: var(--color-primary);",
    "  --brand-secondary: var(--color-text-primary);",
    "  --brand-accent: var(--color-accent);",
    "  --brand-background: var(--color-background);",
    "  --brand-text: var(--color-text-primary);",
    "  --brand-surface: var(--color-surface);",
    "  --brand-border: var(--color-border);",
    "  --brand-muted: var(--color-text-secondary);",
    "  --commerce-price: var(--color-text-primary);",
    "  --commerce-sale-badge: var(--color-sale);",
    "  --commerce-sale: var(--color-sale);",
    `  --commerce-sale-badge-fg: ${saleFg};`,
    "  --content-text: var(--color-text-primary);",
    "  --content-muted: var(--color-text-secondary);",
    "  --content-heading: var(--color-text-primary);",
    "  --nav-action: var(--color-primary);",
    `  --nav-action-fg: ${primaryFg};`,
    "  --nav-action-hover: var(--color-primary-dark);",
    "  --nav-avatar: var(--color-surface-secondary);",
    "  --nav-avatar-fg: var(--color-text-primary);",
    "  --action-primary: var(--color-primary);",
    "  --action-primary-hover: var(--color-primary-dark);",
    `  --action-primary-fg: ${primaryFg};`,
    "  --action-secondary: var(--color-surface-secondary);",
    "  --action-secondary-fg: var(--color-text-primary);",
  ].join("\n");

  // `:root` fallback — resolved hex values from the SAME v2 tokens (var()
  // refs would not resolve here). Keeps admin/platform inherit-from-root
  // behaviour that predates Design System v2.
  const rootLines = [
    `  --brand-primary: ${tokens.primary};`,
    `  --brand-secondary: ${tokens.text_primary};`,
    `  --brand-accent: ${tokens.accent};`,
    `  --brand-background: ${tokens.background};`,
    `  --brand-text: ${tokens.text_primary};`,
    `  --brand-surface: ${tokens.surface};`,
    `  --brand-border: ${tokens.border};`,
    `  --brand-muted: ${tokens.text_secondary};`,
    `  --commerce-price: ${tokens.text_primary};`,
    `  --commerce-sale-badge: ${tokens.sale};`,
    `  --commerce-sale: ${tokens.sale};`,
    `  --commerce-sale-badge-fg: ${saleFg};`,
    `  --content-text: ${tokens.text_primary};`,
    `  --content-muted: ${tokens.text_secondary};`,
    `  --content-heading: ${tokens.text_primary};`,
    `  --nav-action: ${tokens.primary};`,
    `  --nav-action-fg: ${primaryFg};`,
    `  --nav-action-hover: ${tokens.primary_dark};`,
    `  --nav-avatar: ${tokens.surface_secondary};`,
    `  --nav-avatar-fg: ${tokens.text_primary};`,
    `  --action-primary: ${tokens.primary};`,
    `  --action-primary-hover: ${tokens.primary_dark};`,
    `  --action-primary-fg: ${primaryFg};`,
    `  --action-secondary: ${tokens.surface_secondary};`,
    `  --action-secondary-fg: ${tokens.text_primary};`,
  ].join("\n");

  const css =
    `.storefront-shell {\n${colorLines}\n${legacyAliasLines}\n}\n` +
    `:root {\n${rootLines}\n}`;

  let el = document.getElementById("brand-color-tokens");
  if (!el) {
    el = document.createElement("style");
    el.id = "brand-color-tokens";
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

function applyThemeVariables(theme) {
  // Phase 1 (Design System v2 SSOT): all COLOR derivation moved to
  // `applyStorefrontColorTokens()`, which reads from resolved v2 tokens
  // ONLY. This function now handles only the non-color runtime effects:
  //   - Radius (from `theme.button_style` — not a color)
  //   - Body inline-style reset (existing isolation guard)
  //   - Typography preset / Google Fonts injection
  //
  // Legacy `theme.primary/secondary/accent/background/text` NEVER drives
  // any CSS variable in this file anymore. If the backend payload lacks a
  // `design_tokens` object, `resolveDesignTokens(payload)` folds the legacy
  // 5-field theme into v2 tokens first — legacy remains a data-input
  // source, not a CSS-var source.
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--brand-radius", radiusFor(theme.button_style));
  // Branding V2 — TEXT/BG ISOLATION: JANGAN tulis warna ke <body> (global).
  // Warna teks & background branding hanya berlaku di storefront scope
  // (.storefront-shell / Layout). Login, admin, platform punya design system
  // sendiri (.admin-scope / .platform-scope) dan tidak boleh terpengaruhi.
  if (document.body) {
    document.body.style.color = "";
    document.body.style.backgroundColor = "";
  }

  // Branding V2 — TYPOGRAPHY PRESET (storefront only). Preset eksplisit
  // menang atas font template; tanpa preset → font template (existing).
  // Phase 3: preset "custom" → font whitelist tenant + cakupan heading diperluas; selain itu jalur lama identik.
  const typo = resolveTypography(theme);
  applyBrandFonts(typo.heading, typo.body, typo.extended);
}

// Template typography runtime — load Google Fonts + override heading/body font.
export const EXTENDED_HEADING_SELECTORS = [
  ".storefront-shell h1", ".storefront-shell h2", ".storefront-shell h3", ".storefront-shell .font-display",
  ".storefront-shell .storefront-section-title", ".storefront-shell .cp-hero-title", ".storefront-shell .cp-stat-value",
  ".storefront-shell .cp-biz-title", ".storefront-shell .gcp-cta-title", ".storefront-shell .gcp-footer-heading",
];

function applyBrandFonts(heading, body, extended = false) {
  if (typeof document === "undefined") return;
  const fams = [...new Set([heading, body].filter(Boolean))];
  if (!fams.length) return;
  const href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${encodeURIComponent(f)}:wght@400;500;600;700;800`).join("&")}&display=swap`;
  let link = document.getElementById("brand-google-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "brand-google-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
  let style = document.getElementById("brand-font-style");
  if (!style) {
    style = document.createElement("style");
    style.id = "brand-font-style";
    document.head.appendChild(style);
  }
  // Branding V2 — font branding di-scope ke storefront saja (.storefront-shell
  // + auth shell). Admin/platform TIDAK ikut berubah font.
  const rules = [];
  if (body) rules.push(`.storefront-shell{font-family:'${body}',ui-sans-serif,system-ui,sans-serif;}`);
  if (heading) {
    const sel = extended ? EXTENDED_HEADING_SELECTORS.join(",") : ".storefront-shell .font-display";
    rules.push(`${sel}{font-family:'${heading}',ui-sans-serif,system-ui,sans-serif !important;}`);
  }
  style.textContent = rules.join("\n");
}

function applyDocumentMeta(branding) {
  if (typeof document === "undefined") return;
  const { general, visual, theme } = branding;
  // Title
  if (general.name) document.title = general.name;
  // Favicon — ganti SEMUA icon link statis (16/32/96/apple-touch dari index.html)
  // agar tidak ada favicon warisan yang menang.
  // ROOT CAUSE FIX: path upload ({tenant}/{uuid}.png) WAJIB di-resolve via
  // imageUrl() → /api/files/... ; tanpa ini href jadi path relatif yang jatuh
  // ke SPA catch-all (HTML) dan browser mempertahankan favicon lama.
  // Cache-busting deterministik: setiap upload menghasilkan filename uuid unik
  // (URL immutable) — suffix ?v=b2 statis hanya untuk menang atas cache favicon
  // legacy, stabil antar render (tidak mematikan cache).
  if (visual.favicon) {
    const resolved = imageUrl(visual.favicon);
    const bust = resolved.includes("?") ? resolved : `${resolved}?v=b2`;
    document
      .querySelectorAll("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
      .forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    const icon = document.createElement("link");
    icon.rel = "icon";
    icon.href = bust;
    icon.setAttribute("data-managed", "tenant-favicon");
    document.head.appendChild(icon);
    // Sprint Tenant PWA Icon v1 — apple-touch-icon (iOS Add-to-Home-Screen)
    // prefers `visual.pwa_icon` (uploaded via Tenant Admin → Branding →
    // Mobile App / PWA) over `visual.favicon`.  Fallback chain:
    //   pwa_icon → favicon → default.  Normal browser favicon contract
    // (link[rel="icon"]) is untouched — still uses `visual.favicon`.
    const appleSrc = visual.pwa_icon || visual.favicon;
    const appleResolved = imageUrl(appleSrc);
    const appleBust = appleResolved.includes("?") ? appleResolved : `${appleResolved}?v=b2`;
    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    apple.href = appleBust;
    apple.setAttribute("data-managed", "tenant-favicon");
    document.head.appendChild(apple);
  }
  // Theme color
  let themeMeta = document.querySelector("meta[name='theme-color']");
  if (!themeMeta) {
    themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    document.head.appendChild(themeMeta);
  }
  themeMeta.content = theme.primary;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const host = useHost();
  // HOTFIX P0 — Platform Branding Synchronization.
  // Tenant BrandingContext hanya boleh menulis <title>/favicon ketika host
  // sebenarnya bertipe tenant (mis. sinar-telur.dynicty.com). Pada platform
  // host (app.dynicty.com / preview / localhost), Platform Owner Portal
  // memakai identitas Dynicty via PlatformBrandingContext — tenant branding
  // TIDAK boleh menimpa title/favicon platform.
  const isTenantHost = host?.kind === "tenant";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/public/branding");
      if (r.data && typeof r.data === "object") {
        setBranding(r.data);
      }
    } catch { /* keep defaults on failure */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply CSS vars whenever branding changes. Document meta (title/favicon)
  // dilewati bila bukan tenant host — mencegah "Sinar Telur" nyasar ke tab
  // Platform Owner Portal.
  useEffect(() => {
    applyThemeVariables(branding.theme);
    // Design System v2 (Phase 2) — inject the 10 canonical `--color-*` vars
    // scoped to `.storefront-shell`. Consumes `branding.design_tokens` from
    // backend Phase 1 payload, with graceful fallback to legacy `theme.*` +
    // system defaults when the payload is missing.
    applyStorefrontColorTokens(resolveDesignTokens(branding));
    if (isTenantHost) applyDocumentMeta(branding);
  }, [branding, isTenantHost]);

  const value = useMemo(() => ({ branding, loading, reload: load }), [branding, loading, load]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}

/** Convenience hooks for common fields */
export function useBrandName() {
  return useContext(BrandingContext).branding.general.name;
}
